//
// Copyright (c) 2026 Comcast
//

import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import { setVisible } from 'reducers/modalForm'
import * as modalFormSelector from 'selectors/modalForm'
import * as sessionUserSelector from 'selectors/sessionUser'

import ModalFormRenderer from './ModalFormRenderer'
import { usePrevious } from 'utils/hooks'

const ModalForm = ({
  visible, onClose, onBeforeSubmit, onSubmit, initialValues, title, children, confirmOnClose,
  confirmOnSubmit, confirmBodyComponent, showJustificationOnConfirm, actionButtonTitle,
  additionalButton, mutators, disableActionButton, fieldParams, formValidator,
  subscribeToValues, tabs, initialTabId, flatFieldNames,
}) => {
  const [visible_, setVisible_] = useState(false)
  const operationActive = useSelector(modalFormSelector.operationActive)
  const operationSuccessful = useSelector(modalFormSelector.operationSuccessful)
  const userTimeZone = useSelector(sessionUserSelector.timezone)
  const prevOperationActive = usePrevious(operationActive)
  const dispatch = useDispatch()

  useEffect(() => {
    setVisible_(visible)
    dispatch(setVisible(visible))
  }, [visible]) // eslint-disable-line react-hooks/exhaustive-deps

  // close modal when operation is completed successfully
  // check prevOperationActive to prevent closing modal on start
  useEffect(() => {
    if (prevOperationActive && !operationActive && operationSuccessful) {
      setVisible_(false)
      onClose()
    }
  }, [operationActive]) // eslint-disable-line react-hooks/exhaustive-deps

  const closeForm = () => {
    setVisible_(false)
    if (onClose) {
      onClose()
    }
  }

  const passThroughProps = {
    onBeforeSubmit,
    onSubmit,
    initialValues,
    title,
    children,
    confirmOnClose,
    confirmOnSubmit,
    confirmBodyComponent,
    showJustificationOnConfirm,
    actionButtonTitle,
    additionalButton,
    mutators,
    disableActionButton,
    fieldParams,
    formValidator,
    subscribeToValues,
    tabs,
    initialTabId,
    flatFieldNames,
    userTimeZone,
  }

  return (
    <ModalFormRenderer
      visible={visible_}
      closeForm={closeForm}
      loading={operationActive}
      {...passThroughProps}
    />
  )
}

ModalForm.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onBeforeSubmit: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  confirmOnClose: PropTypes.bool,
  confirmOnSubmit: PropTypes.bool,
  confirmBodyComponent: PropTypes.func,
  showJustificationOnConfirm: PropTypes.bool,
  actionButtonTitle: PropTypes.string,
  additionalButton: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  mutators: PropTypes.object,
  disableActionButton: PropTypes.bool,
  fieldParams: PropTypes.array,
  formValidator: PropTypes.func,
  subscribeToValues: PropTypes.bool,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
    })
  ),
  initialTabId: PropTypes.string,
  flatFieldNames: PropTypes.bool,
}

export default ModalForm
